<template>
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
      variant="secondary"
    />
  </div>
  <div
    v-else
    ref="contentRef"
    class="page"
  >
    <riseup-header :flow-config-id="otsJobConfig.flowConfigId" />
    <div ref="content">
      <missing-data
        v-if="shouldShowMissingData"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <div v-if="userFinanceState === SALARY_FINANCE_FLOWS.borrowAndSave">
        <six-months-summary
          :monthly-avg-income-spending="monthlyAvgIncomeSpending"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          :is-data-missing="isSixMonthsDataMissing"
          flow-type="borrowAndSave"
          @button-click="() => navigateToSalaryFinance('LOAN')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <spending-categories
          :monthly-category-averages="monthlyCategoryAverages"
          :is-data-missing="isSpendingCategoriesDataMissing"
          @button-click="() => navigateToSalaryFinance('LOAN')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <tips
          :tips="tips"
          :total-savings="totalSavings"
          :is-data-missing="isTipsDataMissing"
          :show-background="!shouldShowMissingData"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <div v-if="!shouldShowMissingData" class="page-section light-blue-section">
          <h1
            class="info-title red font-32"
          >
            Your monthly <b>loan repayment potential</b>
          </h1>
          <h1 class="top-title">
            <span>£</span>{{ numberToHumanReadable(monthlyAvgDisposableIncome.total) }}
          </h1>
          <div class="section-subtitle light-section-text">
            Based on your income and spending, you may have <b>£{{ numberToHumanReadable(monthlyAvgDisposableIncome.total) }}</b> available monthly
            – this is what you might afford to repay if you're considering a low-cost loan.
          </div>
          <button
            class="blue-button width-80"
            @click="() => navigateToSalaryFinance('LOAN')"
          >
            Apply now <span class="arrow-right">→</span>
          </button>
        </div>
        <div v-if="!shouldShowMissingData" class="page-section white-section">
          <div class="info-title red">
            Choose a loan that <b>suits you</b>
          </div>

          <div class="section-subtitle">
            Explore a low-cost Salary Finance loan, with repayments collected directly from your pay.
          </div>
          <div class="breakdown-table">
            <div class="breakdown-header">
              <h1><b>£3,000</b> over 18 months</h1>
            </div>
            <div class="divider" />
            <div class="breakdown-content">
              <div class="breakdown-row-no-border">
                <div class="col source">
                  Monthly cost
                </div>
                <div class="col invest">
                  <b>£184.46</b>
                </div>
              </div>

              <div class="breakdown-row-no-border">
                <div class="col source">
                  Total repayment
                </div>
                <div class="col invest">
                  <b>£3,320.33</b>
                </div>
              </div>

              <div class="breakdown-row-no-border">
                <div class="col source">
                  Total interest
                </div>
                <div class="col invest">
                  <b>£320.33</b>
                </div>
              </div>

              <div class="breakdown-row-no-border">
                <div class="col source">
                  <b>13.9%</b> APR Representative
                </div>
              </div>
            </div>
            <button
              class="blue-button"
              @click="() => navigateToSalaryFinance('LOAN')"
            >
              Apply now <span class="arrow-right">→</span>
            </button>
            <button
              class="secondary-button"
              @click="() => navigateToSalaryFinance('LOAN')"
            >
              View other options
            </button>
          </div>
          <div class="blue-background-color" />
        </div>
        <savings-outlook
          v-if="!shouldShowMissingData"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          :add-padding="false"
          @button-click="() => navigateToSalaryFinance('START_SAVING')"
        />
        <smart-savings v-if="!shouldShowMissingData" @button-click="() => navigateToSalaryFinance('START_SAVING')" />
      </div>
      <div v-else-if="userFinanceState === SALARY_FINANCE_FLOWS.saveNoAccount">
        <six-months-summary
          :monthly-avg-income-spending="monthlyAvgIncomeSpending"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          :is-data-missing="isSixMonthsDataMissing"
          flow-type="saveNoAccount"
          @button-click="() => navigateToSalaryFinance('START_SAVING')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <spending-categories
          :monthly-category-averages="monthlyCategoryAverages"
          :is-data-missing="isSpendingCategoriesDataMissing"
          @button-click="() => navigateToSalaryFinance('START_SAVING')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <tips
          :tips="tips"
          :total-savings="totalSavings"
          :is-data-missing="isTipsDataMissing"
          :show-background="!shouldShowMissingData"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <div v-if="!shouldShowMissingData" class="page-section light-blue-section">
          <h1 class="info-title red">
            Your monthly <b>saving potential</b>
          </h1>
          <h1 class="top-title">
            <span>£</span>{{ numberToHumanReadable(monthlyAvgDisposableIncome.total) }}
          </h1>
          <div class="section-subtitle light-section-text">
            This is the amount you might be able to save monthly, based on your income and spending habits.
          </div>
          <button
            class="blue-button width-90"
            @click="() => navigateToSalaryFinance('START_SAVING')"
          >
            Start saving <span class="arrow-right">→</span>
          </button>
        </div>
        <savings-outlook
          v-if="!shouldShowMissingData"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          @button-click="() => navigateToSalaryFinance('START_SAVING')"
        />
        <smart-savings v-if="!shouldShowMissingData" @button-click="() => navigateToSalaryFinance('START_SAVING')" />
      </div>
      <div v-else>
        <six-months-summary
          :monthly-avg-income-spending="monthlyAvgIncomeSpending"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          :is-data-missing="isSixMonthsDataMissing"
          flow-type="noBorrowAndSave"
          :show-additional-text="true"
          :show-button="true"
          @button-click="() => navigateToSalaryFinance('FINANCIAL_SUPPORT')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <spending-categories
          :monthly-category-averages="monthlyCategoryAverages"
          :is-data-missing="isSpendingCategoriesDataMissing"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <tips
          :tips="tips"
          :total-savings="totalSavings"
          :is-data-missing="isTipsDataMissing"
          :show-background="!shouldShowMissingData"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <div v-if="!shouldShowMissingData" class="page-section light-blue-section">
          <h1 class="info-title red">
            More tools to <b>help you master your money</b>
          </h1>
          <p class="section-subtitle">
            Explore our planning & budgeting content to help you save more
          </p>
          <div class="image-container">
            <img
              src="../../../assets/salary-finance/take-control-video-icon.svg"
              @click="() => navigateToSalaryFinance('TAKE_CONTROL')"
            >
            <img
              src="../../../assets/salary-finance/smart-habits-video-icon.svg"
              @click="() => navigateToSalaryFinance('SMART_HABITS_VIDEO')"
            >
            <img
              src="../../../assets/salary-finance/money-health-check-icon.svg"
              @click="() => navigateToSalaryFinance('MONEY_HEALTH_CHECK')"
            >
          </div>
          <div class="blue-background-color" />
        </div>
        <div v-if="!shouldShowMissingData" class="page-section">
          <h1 class="info-title white">
            Financial help
          </h1>
          <p class="section-subtitle">
            Get impartial support from organisations that can assist in many ways.
          </p>
          <div
            class="breakdown-table financial-support"
          >
            <div
              class="breakdown-header financial-support-header"
            >
              <h3>Citizens Advice</h3>
            </div>
            <div class="breakdown-content">
              <div class="breakdown-row-no-border">
                <div class="col source">
                  Free, confidential and independent advice
                </div>
              </div>
            </div>
            <button
              class="blue-button margin-top-20"
              @click="() => navigateToSalaryFinance('CITIZEN_ADVICE')"
            >
              Learn more
            </button>
          </div>
          <div
            class="breakdown-table financial-support"
          >
            <div
              class="breakdown-header financial-support-header"
            >
              <h3>Turn2us</h3>
            </div>
            <div class="breakdown-content">
              <div class="breakdown-row-no-border">
                <div class="col source">
                  Helping people identify the benefits and tax credits they can claim
                </div>
              </div>
            </div>
            <button
              class="blue-button margin-top-20"
              @click="() => navigateToSalaryFinance('TURN2US')"
            >
              Learn more
            </button>
          </div>
          <div class="button-container">
            <button
              class="blue-button white-border"
              @click="() => navigateToSalaryFinance('FINANCIAL_SUPPORT')"
            >
              More options <span
                class="arrow-right vertical-align-middle"
              >→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <error-toast
    v-if="showErrorToast"
    :text="errorMessage"
    :reason="errorReason"
    @close="showErrorToast = false"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import config from '../../../config';
import Segment from '../../../Segment';
import { useCashflow } from '../../../services/cashflowService';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import { numberToHumanReadable } from '../../../utils/miscUtils';
import ErrorToast from '../../Common/ErrorToast.vue';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import MissingData from '../shared-components/MissingData.vue';
import SixMonthsSummary from '../shared-components/SixMonthsSummary.vue';
import SpendingCategories from '../shared-components/SpendingCategories.vue';
import Tips from '../shared-components/Tips.vue';
import SavingsOutlook from './components/SavingsOutlook.vue';
import SmartSavings from './components/SmartSavings.vue';

const { insights, populateInsights } = useInsights();
const contentRef = ref(null);
const route = useRoute();
const { getOtsJobConfig } = useOtsJob();
const {
  monthlyCategoryAverages,
  monthlyAvgDisposableIncome,
  monthlyAvgIncomeSpending,
  loadCashflowData,
  isCashflowLoaded,
} = useCashflow();
const SALARY_FINANCE_FLOWS = {
  borrowAndSave: 'borrowAndSave',
  saveNoAccount: 'saveNoAccount',
  noBorrowAndSave: 'noBorrowAndSave',
};
const SALARY_FINANCE_DESTINATION = {
  LOAN: 'sf_loan',
  START_SAVING: 'sf_start_saving',
  FINANCIAL_SUPPORT: 'sf_financial_support',
  TAKE_CONTROL: 'sf_take_control',
  SMART_HABITS_VIDEO: 'sf_smart_habits_video',
  MONEY_HEALTH_CHECK: 'sf_money_health_check',
  CITIZEN_ADVICE: 'sf_citizen_advice',
  TURN2US: 'sf_turn2us',
};
const ERROR_MESSAGES = {
  INSIGHTS_ONLY: 'Failed to load your saving tips. We are looking into it. Try again later.',
  CASHFLOW_ONLY: 'Oops! There was an error with your account data. We are looking into it. Try again later.',
};
const TOP_THRESHOLD = 300;
const MID_THRESHOLD = 50;

const tips = ref([]);
const totalSavings = ref(0);
const otsJobConfig = await getOtsJobConfig();
const sessionStore = useSessionStore();
const showErrorToast = ref(false);
const errorMessage = ref('');
const errorReason = ref('');

const {
  showContentLoader,
  toggleLoader,
} = useContentLoader();

// Centralized missing data logic
const isSixMonthsDataMissing = computed(() =>
  monthlyAvgIncomeSpending.value.income === 0
  || monthlyAvgIncomeSpending.value.spending === 0
  || monthlyAvgDisposableIncome.value.total === 0);

const isSpendingCategoriesDataMissing = computed(() =>
  monthlyCategoryAverages.value.length < 3);

const isTipsDataMissing = computed(() => tips.value.length === 0);

const shouldShowMissingData = computed(() =>
  isSixMonthsDataMissing.value
  || isSpendingCategoriesDataMissing.value
  || isTipsDataMissing.value);

const handleErrors = (insightsFailed, cashflowFailed) => {
  if (!insightsFailed && !cashflowFailed) {
    return;
  }

  if (cashflowFailed) {
    errorMessage.value = ERROR_MESSAGES.CASHFLOW_ONLY;
    errorReason.value = 'cashflow_data_load_failed';
  } else {
    errorMessage.value = ERROR_MESSAGES.INSIGHTS_ONLY;
    errorReason.value = 'insights_data_load_failed';
  }

  showErrorToast.value = true;
};

onMounted(async () => {
  toggleLoader(true);
  const queryParam = route.query || null;
  const shouldReload = route.state?.shouldReload;

  let insightsFailed = false;
  let cashflowFailed = false;

  if (!insights.value || insights.value.length === 0 || shouldReload) {
    try {
      await populateInsights(otsJobConfig.jobId);
    } catch (error) {
      insightsFailed = true;
    }
  }

  Segment.trackUserGot('SalaryFinanceOtsCustomSuccessPageEntered', { fromEmail: queryParam ? queryParam.fromEmail : false, otsJobConfig });

  if ((insights.value && insights.value.length > 0) || shouldReload) {
    tips.value = insights.value.map(insight => ({
      text: insight.item.text,
      amount: insight.item.amount,
      index: insight.index,
    }));

    totalSavings.value = insights.value.reduce((sum, insight) => sum + insight.item.amount, 0);
  }

  if (!isCashflowLoaded.value || shouldReload) {
    try {
      await loadCashflowData(6);
    } catch (error) {
      cashflowFailed = true;
    }
  }

  handleErrors(insightsFailed, cashflowFailed);
  toggleLoader(false);
});

const navigateToSalaryFinance = (destination = 'LOAN') => {
  const { customerId } = sessionStore;
  window.open(`${config.get().ruthUrl}/ots?customer_id=${customerId}&to=${SALARY_FINANCE_DESTINATION[destination]}`, '_blank');
};

const userFinanceState = computed(() => {
  if (monthlyAvgDisposableIncome.value.total >= TOP_THRESHOLD) {
    return SALARY_FINANCE_FLOWS.borrowAndSave;
  }
  if (monthlyAvgDisposableIncome.value.total < TOP_THRESHOLD && monthlyAvgDisposableIncome.value.total > MID_THRESHOLD) {
    return SALARY_FINANCE_FLOWS.saveNoAccount;
  }
  return SALARY_FINANCE_FLOWS.noBorrowAndSave;
});

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('SalaryFinanceConnectOtsAccountsConnectClicked', { gclid: Cookies.get('gclid') });
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId, fromSuccess: true }, config.get().otsSalaryFinanceSuccessEnv);
};
</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.content {
  margin-top: 30px;
  padding-left: 25px;
  padding-right: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

h1 {
  font-weight: 100;
}

h1 b {
  font-weight: bold !important;
}

.page {
  background-color: #012577;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  font-family: 'SalaryFinance';
}

.page-section {
  color: white;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 35px;
}

.section-subtitle {
  font-size: 17px;
  font-family: 'SalaryFinance';
  margin-top: 25px;
  margin-bottom: 40px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.info-title {
  font-size: 36px;
  font-family: 'SalaryFinance';
  padding-top: 40px;
  padding-right: 25px;
  margin-bottom: 20px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
  line-height: 1;
}

.white {
  color: #FFFFFF;
}

.red {
  color: #F14241;
}

.font-32 {
  font-size: 32px;
}

.info-title b {
  font-weight: 700;
}

.divider {
  width: 100%;
  margin: 30px auto;
  height: 1px;
  border-bottom: 1px solid #002579;
  opacity: 20%;
}

.blue-button {
  background-color: #002579;
  color: white;
  width: 100%;
  border-radius: 12px;
  padding: 10px 0px;
  border: none;
  cursor: pointer;
  font-size: 18px;
  font-family: 'SalaryFinance';
  font-weight: 700;
  margin-top: 40px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  padding-top: 16px;
  padding-bottom: 18px;
}

.width-80 {
  width: 80%;
}

.width-90 {
  width: 90%;
}

.margin-top-20 {
  margin-top: 20px;
}

.secondary-button {
  background-color: #FFFFFF;
  border: 1px solid #002579;
  color: #002579;
  width: 100%;
  border-radius: 12px;
  cursor: pointer;
  font-size: 18px;
  font-family: 'SalaryFinance';
  font-weight: 700;
  margin-top: 15px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  padding-top: 19px;
  padding-bottom: 21px;
}

.button-container {
  width: 90%;
  margin: 0 auto;
}

.white-border {
  border: 1px solid #FFFFFF
}

.image-container {
  width: 90%;
  margin: 0 auto;
}

.image-container img {
  width: 100%;
  margin: 15px auto;
  position: relative;
  z-index: 1;
}

.light-blue-section {
  background-color: #EEF2F8;
  color: #002579;
}

.white-section {
  background-color: #FFFFFF;
  color: #002579;
}

.arrow-right {
  margin-left: 10px;
  font-family: 'SalaryFinance';
  color: #FD5F5A;
  font-size: 30px;
  vertical-align: sub;

  &.vertical-align-middle {
    vertical-align: middle;
  }
}

.top-title {
  font-size: 67px;
  font-family: 'SalaryFinance';
  line-height: 1;
  color: #002579;
  width: 80%;
  margin-left: 10%;
  text-align: left;
  margin-bottom: 20px;
}

.light-section-text {
  color: #002579;
}

.breakdown-table {
  width: 90%;
  background-color: white;
  box-shadow: 0px 16px 46.4px 0px #00000026;
  border-radius: 25px;
  padding: 25px;
  margin-top: 20px;
  z-index: 2;
  margin: 0 auto;
  position: relative;
  padding-top: 30px;
}

.breakdown-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  h1 {
    margin: 0;
    color: #002579;
    font-size: 25px;
  }

  h2 {
    margin: 0;
    color: #002579;
    font-size: 20px;
    font-weight: 700;
    margin-left: 10px;
  }

  h3 {
    margin: 0;
    color: #F14241;
    font-size: 20px;
  }

  .graph-icon {
    width: 20px;
    height: 20px;
  }
}

.breakdown-content {
  width: 100%;
}

.breakdown-row-no-border {
  display: flex;
  padding: 5px 5px;
  font-family: 'SalaryFinance';
}

.col {
  color: #0C3467;

  &.source {
    flex: 4;
    text-align: left;
  }

  &.total {
    flex: 1;
  }

  &.invest {
    flex: 1;
    text-align: right;
    font-weight: 700;
  }
}

.financial-support {
  margin: 20px auto;
  padding: 5px 20px;
}

.financial-support-header {
  padding-top: 20px;
}

.blue-background-color {
  background-color: #002579;
  height: 100px;
  z-index: 0;
  position: absolute;
  bottom: 0;
  width: 100%;
}
</style>
